import styled from 'styled-components';
import { css } from '@styled-system/css';
import { useRouter } from 'next/router';
import {
  HuiButton,
  HuiButtonLayoutProps,
  HuiDialog,
  HuiDialogBody,
} from '@humaninterest/hui';
import { useState } from 'react';

import { lcSideFilterProps } from 'humanity/components/lcSideFilter/lcSideFilter.types.js';
import Box from 'humanity/primitives/box';
import Flex from 'humanity/primitives/flex';
import { InputCheckbox } from 'humanity/components/inputCheckbox';
import { backendToFrontendFilter } from 'utils/learningCenterFilterNames';
import { theme } from 'utils/theme.v2';

const StyledBox = styled(Box).attrs({ as: 'form' })(
  css({
    bg: ['white', null, null, 'backgroundLightGray2'],
    p: [0, null, null, 5],
    borderRadius: 'sm',
    overflowY: 'scroll',
    position: ['inherit', 'sticky'],
    top: '158px',
    '-ms-overflow-style': 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  })
);

const StyledHeading = styled('div')(
  css({
    fontSize: 1,
    textTransform: 'uppercase',
    fontWeight: 'bold',
    color: theme.colors.blue100,
    mb: '20px',
  })
);

const StyledFieldSet = styled('fieldset')(
  css({
    fontSize: 1,
    border: 'none',
    mx: 0,
    mt: '20px',
    padding: 0,
  })
);

const StyledCategory = styled(Flex)(
  css({
    flexDirection: 'column',
    justifyContent: 'center',
    color: theme.colors.blue50,
    p: 0,
  })
);

const StyledButton = styled(HuiButton)(
  css({
    display: ['block', null, null, 'none'],
    borderColor: theme.colors.blue20,
    fontWeight: 'normal',
    textAlign: 'left',
    borderWidth: '3px',
    mt: 2,
    mb: 5,
  })
);

const FilterTypes = ({ filterTypes, handleFilter }) => {
  const { query } = useRouter();

  return filterTypes?.map(({ displayName, name, filtersCollection }) => (
    <StyledFieldSet key={name}>
      <StyledCategory as="legend">{displayName}</StyledCategory>
      {filtersCollection?.items?.map(
        ({ name: filterName, displayName: filterDisplayName }) => {
          const checked = query[backendToFrontendFilter(name)]
            ?.split(',')
            ?.includes(filterName);

          return (
            <InputCheckbox
              key={filterName}
              id={filterName}
              text={filterDisplayName}
              checkboxProps={{ checked: !!checked }}
              onChange={(e) => handleFilter(e, name)}
            />
          );
        }
      )}
    </StyledFieldSet>
  ));
};

FilterTypes.propTypes = {
  ...lcSideFilterProps,
};

const LcSideFilter = ({ handleFilter, filterTypes, ...props }) => {
  const [showModal, setShowModal] = useState(false);
  const handleClick = () => setShowModal(!showModal);
  return (
    <StyledBox {...props}>
      <StyledButton layout={HuiButtonLayoutProps.Outline} onClick={handleClick}>
        Filter by
      </StyledButton>
      <Box display={['none', null, null, 'block']}>
        <StyledHeading>Filter By</StyledHeading>
        <FilterTypes handleFilter={handleFilter} filterTypes={filterTypes} />
      </Box>
      <Box display={['block', null, null, 'none']}>
        {showModal && (
          <HuiDialog
            hasCloseButton
            onClose={() => {
              setShowModal(false);
            }}
            testId="dialog-with-close"
          >
            <HuiDialogBody>
              <FilterTypes handleFilter={handleFilter} filterTypes={filterTypes} />
            </HuiDialogBody>
          </HuiDialog>
        )}
      </Box>
    </StyledBox>
  );
};

LcSideFilter.propTypes = {
  ...lcSideFilterProps,
};

export default LcSideFilter;
